import { html, property } from 'lit-element';
import { nothing } from 'lit-html';
import {
  event,
  EventEmitter,
  KatLitElement,
  Keys,
  register,
} from '../../shared/base';
import { KatCheckbox } from '../checkbox';
import { KatRadiobutton } from '../radiobutton';
import { checkSlots } from '../../shared/slot-utils';
import baseStyles from '../../shared/base/base.lit.scss';
import styles from './selection-card.lit.scss';

/**
 * @component {kat-selection-card} KatSelectionCard Selection Cards allow users to select either a single option from several others (Single select card: Radio button) or allow users to evaluate each option independently and select one, multiple, or none (Multi-select card: Checkbox). They are collected into groups by the Selection Card Group component.
 * @guideline Do Use radio button card when there are more than 2 options, but the user may only select one.
 * @guideline Do When using radio button card, have one option selected by default, preferably the recommended or best option. This default must be safe from the user’s perspective.
 * @guideline Do Use checkbox card when the user may choose one or more options from a limited number of cards.
 * @guideline Don't use radio button cards for multi-select. Use checkbox cards instead.
 * @guideline Don’t use checkbox cards when a user needs to select only one option. Use radio button cards instead.
 * @guideline Don’t use selection cards to perform any actions, as they already have a toggle (checkbox/radio button) control.
 * @guideline Don't use a single selection card. They should always appear as a group.
 * @guideline Don’t use overflow menus or buttons in selection cards.
 * @status Production
 * @theme flo
 * @example Basic {"multiple": "true", "header": "Selection Card", "subheader": "Select a Kat", "body": "Selecting a kat informs us which kind of kat you like.", "footer": "Please select a kat. We would really like that.", "value": "kat", "imageSrc": "https://m.media-amazon.com/images/G/01/katal/internal/SquareCat1.jpg"}
 * @example BasicRadio {"multiple": "false", "header": "Selection Card", "subheader": "Select a Kat", "body": "Selecting a kat informs us which kind of kat you like.", "footer": "Please select a kat. We would really like that.", "value": "kat", "imageSrc": "https://m.media-amazon.com/images/G/01/katal/internal/SquareCat1.jpg"}
 * @example Slotted { "content": "<img slot=\"image\" src=\"https://m.media-amazon.com/images/G/01/katal/internal/SquareCat1.jpg\" alt=\"kitty kat\" /><span>Select a Kat</span><span slot=\"subheader\">Subheader text</span><span slot=\"body\">Body text</span><span slot=\"footer\">Footer text</span>"}
 * @slot default Elements without a named slot attribute appear in the header.
 * @slot subheader Subheader element
 * @slot body Body element
 * @slot footer Footer element
 * @slot image Image element
 * @a11y {keyboard}
 * @a11y {sr}
 * @a11y {contrast}
 */
@register('kat-selection-card')
export class KatSelectionCard extends KatLitElement {
  /**
   * Title text that will be displayed to the user.
   * A title must be provided by either the header attribute or the default (unnamed) slot.
   */
  @property()
  header?: string;

  /** Additional subheader text to be displayed. */
  @property()
  subheader?: string;

  /** The primary text of the selection card. */
  @property()
  body?: string;

  /** Footer text of the selection card. */
  @property()
  footer?: string;

  /** Value of the option. */
  @property()
  value: string;

  /** If `true`, styled as a checkbox for multi selection. Otherwise, styled as a radiobutton. */
  @property()
  multiple?: boolean;

  /** Relative or absolute location from which to retrieve an image. */
  @property({ attribute: 'image-src' })
  imageSrc?: string;

  /** Alt text to include with the image. Does nothing if imageSrc is not provided. */
  @property({ attribute: 'image-alt' })
  imageAlt?: string;

  /** Controls element interactivity. */
  @property()
  disabled?: boolean;

  /** Whether the Selection Card is selected or not. */
  @property()
  checked?: boolean;

  /** Emits when a Selection Card is selected or unselected. */
  @event('change', true)
  private _change: EventEmitter<{ checked: boolean; value: string }>;

  static get styles() {
    return [baseStyles, styles];
  }

  updated(changedProperties: Map<string, any>) {
    super.updated(changedProperties);

    if (changedProperties.has('checked')) {
      const checkElement = this.shadowRoot.querySelector<
        KatRadiobutton | KatCheckbox
      >('kat-radiobutton, kat-checkbox');
      checkElement.checked = this.checked;
    }
  }

  private handleRadioSelection() {
    this.checked = true;
  }

  private handleCheckSelection() {
    this.checked = !this.checked;
  }

  private onKeyDown(evt: KeyboardEvent) {
    if (evt.keyCode === Keys.Enter || evt.keyCode === Keys.Space) {
      this.onClick(evt);
    }
  }

  private onClick(evt: MouseEvent | KeyboardEvent) {
    // Swallow internal change event
    evt.stopImmediatePropagation();
    if (this.disabled) {
      return;
    }

    // Set the focus state to the wrapper even when slotted elements the click target
    (evt.currentTarget as HTMLElement).focus();

    // Prevent duplicate change event when checking a checked radio card
    if (
      !this.multiple &&
      this.shadowRoot.contains(evt.currentTarget as Node) &&
      this.checked
    ) {
      return;
    }

    if (!this.multiple) {
      this.handleRadioSelection();
    } else {
      this.handleCheckSelection();
    }

    this._change.emit({ checked: this.checked, value: this.value });
  }

  private renderSelectable() {
    if (this.multiple) {
      return html`<kat-checkbox
        tabindex="-1"
        part="card-selector"
        ?checked=${this.checked}
        ?disabled=${this.disabled}
      ></kat-checkbox>`;
    }

    return html`
      <kat-radiobutton
        tabindex="-1"
        part="card-selector"
        ?checked=${this.checked}
        ?disabled=${this.disabled}
      ></kat-radiobutton>
    `;
  }

  private renderImage() {
    if (checkSlots(this).image) {
      return nothing;
    }

    return html` <img src=${this.imageSrc} alt=${this.imageAlt} /> `;
  }

  render() {
    const hasSlotted = checkSlots(this);

    return html`
      <div
        tabindex=${this.disabled ? '-1' : '0'}
        class="wrapper"
        .value=${this.value}
        @click=${this.onClick}
        @change=${this.onClick}
        @keydown=${this.onKeyDown}
        role=${this.multiple ? 'checkbox' : 'radio'}
        aria-checked=${!!this.checked}
        aria-disabled=${!!this.disabled}
      >
        ${!hasSlotted.image && !this.imageSrc
          ? nothing
          : html`
              <div class="left">
                <div class="image">
                  <slot name="image">${this.renderImage()}</slot>
                </div>
              </div>
            `}
        <div class="right">
          <div class="header">
            <div class="title">
              <slot>${hasSlotted.default ? nothing : this.header}</slot>
            </div>
            ${this.renderSelectable()}
          </div>
          <div class="subtitle">
            <slot name="subheader"
              >${hasSlotted.subheader ? nothing : this.subheader}</slot
            >
          </div>
          <div class="body">
            <slot name="body">${hasSlotted.body ? nothing : this.body}</slot>
          </div>
          <div class="footer">
            <slot name="footer"
              >${hasSlotted.footer ? nothing : this.footer}</slot
            >
          </div>
        </div>
      </div>
    `;
  }
}
